import React, { useEffect, useState } from 'react';
import * as S from './Table.styled';
import { Table as TableAnt, Tag } from 'antd';
import { ITableProps } from 'features/RequestsList/interfaces';
import dayjs from 'dayjs';
import { uniq } from 'lodash';
import { ColumnsType } from 'antd/lib/table';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import { ILogType, IRequest } from 'interfaces/main';
import type { FilterValue, SortOrder, SorterResult } from 'antd/es/table/interface';
import { numberWithCommas } from 'utils/help';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';
import { useAppSelector } from 'hooks/useStore';

interface TableParams {
	pagination?: TablePaginationConfig;
	sortField?: string;
	sortOrder?: SortOrder;
	filters?: Record<string, FilterValue>;
}

const Table: React.FC<ITableProps> = ({ items }) => {
	// Table params
	const [tableParams, setTableParams] = useState<TableParams>({
		pagination: {
			current: 1,
			pageSize: 10,
			total: 0,
		},
		// sortField: "createdAt",
		// sortOrder: "descend",
	});

	// Словари
	const { logTypes: allLogTypes } = useAppSelector(LibrariesSelector);

	useEffect(
		() =>
			setTableParams({
				...tableParams,
				pagination: {
					...tableParams.pagination,
					total: items.length,
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[items],
	);

	const emails = uniq(items.map((item: IRequest) => item.email)).map((email: string) => ({
		text: email,
		value: email,
	}));

	const phones = uniq(items.map((item: IRequest) => item.phone)).map((phone: string) => ({
		text: phone,
		value: phone,
	}));

	const names = uniq(items.map((item: IRequest) => item.name)).map((name: string) => ({
		text: name,
		value: name,
	}));

	// Конфиг таблички для Ant Table
	const columns: ColumnsType<IRequest> = [
		{
			title: 'Date',
			dataIndex: 'createdAt',
			key: 'createdAt',
			ellipsis: true,
			sorter: (a: IRequest, b: IRequest) =>
				dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
			defaultSortOrder: 'descend',
			// sortOrder: tableParams.sortField === "date" ? tableParams.sortOrder : null,
			render: (createdAt: string) => dayjs(createdAt).format('DD.MM.YYYY HH:mm'),
			width: 150,
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			filters: names,
			onFilter: (value: string, request: IRequest) => request.name === value,
			width: 200,
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			filters: emails,
			onFilter: (value: string, record) => record.email === value,
			width: 200,
		},
		{
			title: 'Phone',
			dataIndex: 'phone',
			key: 'phone',
			filters: phones,
			onFilter: (value: string, record) => record.phone === value,
			width: 200,
		},
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
		},
		{
			title: 'Location',
			dataIndex: 'location',
			key: 'location',
		},
		{
			title: 'Price',
			dataIndex: 'price',
			key: 'price',
			render: (price: number) => (price ? `${numberWithCommas(price)} USD` : '-'),
		},
		{
			title: 'Type',
			dataIndex: 'typeId',
			key: 'typeId',
			filters: allLogTypes.map((item: ILogType) => ({
				text: item.name,
				value: item.id,
			})),
			render: (typeId: number) =>
				!!typeId ? (
					<Tag bordered={false} color={allLogTypes.find((p: ILogType) => p.id === typeId).antColor}>
						{allLogTypes.find((p: ILogType) => p.id === typeId).name}
					</Tag>
				) : (
					'-'
				),
		},
		// {
		//   title: '',
		//   dataIndex: 'externalID',
		//   key: 'externalID',
		//   render: (externalID: number) => (
		//     <Link
		//       href={`https://www.bayut.com/property/details-${externalID}.html`}
		//       target="_blank">
		//       Advert
		//     </Link>
		//   ),
		// },
	];

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue>,
		sorter: SorterResult<IRequest>,
	) => {
		setTableParams({
			pagination,
			filters,
			...sorter,
		});
	};

	return (
		<S.Wrap>
			<TableAnt
				columns={columns}
				rowKey={(request: IRequest) => request.id}
				dataSource={items}
				pagination={tableParams.pagination}
				onChange={handleTableChange}
			/>
		</S.Wrap>
	);
};

export default Table;
